import { DEFAULT_PROFILE, BLANK_PROFILE, DEFAULT_CONFIG } from '../config/planDefaults';

// Several named plans live in one saved document: the plan on screen at the
// top level, the others in `savedPlans`. Switching swaps them, which is an
// ordinary edit as far as saving and syncing are concerned.
export const MAX_PLANS = 10;
export const DEFAULT_PLAN_ID = 'p1';
export const DEFAULT_PLAN_NAME = 'My Plan';
const NAME_LIMIT = 40;

export const newPlanId = () => `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

export function cleanPlanName(name, fallback = DEFAULT_PLAN_NAME) {
  const text = typeof name === 'string' ? name.replace(/\s+/g, ' ').trim().slice(0, NAME_LIMIT) : '';
  return text || fallback;
}

/** The plan on screen, in the form it is kept while another one is open. */
const shelve = (state, now) => ({
  id: state.planId,
  name: state.planName,
  profile: state.profile,
  simulationConfig: state.simulationConfig,
  savedAt: now,
});

// Results belong to the plan they were computed from.
const withoutResults = { simulationResults: null, spendDownAnalysis: null, heatmapData: [] };

/** Every plan, the open one first. */
export function listPlans(state) {
  return [
    { id: state.planId, name: state.planName, active: true },
    ...state.savedPlans.map((plan) => ({ id: plan.id, name: plan.name, active: false, savedAt: plan.savedAt })),
  ];
}

export const canAddPlan = (state) => state.savedPlans.length + 1 < MAX_PLANS;

function blankProfile() {
  const profile = { ...DEFAULT_PROFILE, ...BLANK_PROFILE };
  ['accounts', 'pension1', 'pension2'].forEach((key) => {
    profile[key] = { ...DEFAULT_PROFILE[key], ...BLANK_PROFILE[key] };
  });
  return profile;
}

/**
 * Starts a new plan and opens it. `source` is 'copy' (the open plan's
 * inputs), 'sample' (the starting sample numbers) or 'blank'.
 */
export function createPlan(state, { id, name, source = 'copy' }, now = Date.now()) {
  if (!canAddPlan(state) || !id) return state;
  let profile = state.profile;
  let simulationConfig = state.simulationConfig;
  if (source === 'sample') {
    profile = DEFAULT_PROFILE;
    simulationConfig = DEFAULT_CONFIG;
  } else if (source === 'blank') {
    profile = blankProfile();
    simulationConfig = { ...DEFAULT_CONFIG, initialInvestment: 0, annualContribution: 0 };
  }
  return {
    ...state,
    ...withoutResults,
    planId: id,
    planName: cleanPlanName(name, `Plan ${state.savedPlans.length + 2}`),
    profile,
    simulationConfig,
    savedPlans: [shelve(state, now), ...state.savedPlans],
  };
}

export function switchPlan(state, id, now = Date.now()) {
  const target = state.savedPlans.find((plan) => plan.id === id);
  if (!target) return state;
  return {
    ...state,
    ...withoutResults,
    planId: target.id,
    planName: target.name,
    profile: target.profile,
    simulationConfig: target.simulationConfig,
    savedPlans: [shelve(state, now), ...state.savedPlans.filter((plan) => plan.id !== id)],
  };
}

export function renamePlan(state, id, name) {
  if (id === state.planId) return { ...state, planName: cleanPlanName(name, state.planName) };
  return {
    ...state,
    savedPlans: state.savedPlans.map((plan) => (plan.id === id ? { ...plan, name: cleanPlanName(name, plan.name) } : plan)),
  };
}

/** Removes a plan. Removing the open one opens the next; the last plan cannot be removed. */
export function deletePlan(state, id) {
  if (id !== state.planId) {
    return { ...state, savedPlans: state.savedPlans.filter((plan) => plan.id !== id) };
  }
  const [next, ...rest] = state.savedPlans;
  if (!next) return state;
  return {
    ...state,
    ...withoutResults,
    planId: next.id,
    planName: next.name,
    profile: next.profile,
    simulationConfig: next.simulationConfig,
    savedPlans: rest,
  };
}
